export interface HeroBoundaryOptions {
  /**
   * FRIDGE-908 — when this returns true (phone widths), the controls also show
   * while the marker is still BELOW the viewport: a visitor an assistant sent
   * to a product page sees the price and the deal button in the first screen
   * instead of after scrolling past a gallery. Desktop keeps the original
   * "after the hero" rule by returning false.
   */
  alsoWhenBelowViewport?: () => boolean;
}

/** Observe geometry as well as intersection: jump scrolling may skip the marker. */
export function observeHeroBoundary(marker: Element, onChange: (passed: boolean) => void, options: HeroBoundaryOptions = {}): () => void {
  let frame = 0;
  let previous: boolean | undefined;
  const measure = () => {
    const rect = marker.getBoundingClientRect();
    const passed = rect.bottom <= 0;
    const below = !!options.alsoWhenBelowViewport?.() && rect.top >= (window.innerHeight ?? 0);
    const show = passed || below;
    if (show !== previous) { previous = show; onChange(show); }
  };
  const schedule = () => {
    if (!frame) frame = window.requestAnimationFrame(() => { frame = 0; measure(); });
  };
  const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(schedule, { threshold: 0 });
  observer?.observe(marker);
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  window.addEventListener('pageshow', schedule);
  measure();
  return () => {
    observer?.disconnect();
    window.cancelAnimationFrame(frame);
    window.removeEventListener('scroll', schedule);
    window.removeEventListener('resize', schedule);
    window.removeEventListener('pageshow', schedule);
  };
}
